import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { Project, Vloerverwarming, Klimaatwand, Klimaatplafond } from '../types';
import cover1 from '../assets/cover-1.png';
import cover2 from '../assets/cover-2.png';
import logo from '../assets/logo-pdf.png';

Font.register({
    family: 'Helvetica-Bold',
    src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
});

const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontFamily: 'Helvetica',
    },
    bold: {
        fontFamily: 'Helvetica-Bold',
    },
    coverPage: {
        padding: 0,
    },
    coverImage: {
        width: '100%',
        height: '100%',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        borderBottomWidth: 1,
        borderBottomColor: '#ccc',
        paddingBottom: 20,
        marginBottom: 20,
    },
    logo: {
        width: 80,
    },
    logoContainer: {
        textAlign: 'center',
    },
    logoText: {
        fontSize: 12,
        marginTop: 5,
    },
    projectInfo: {
        backgroundColor: '#f0f0f0',
        padding: 20,
        borderRadius: 5,
        marginBottom: 20,
        fontSize: 12,
    },
    section: {
        backgroundColor: '#e0e8f0',
        padding: 20,
        borderRadius: 5,
        marginBottom: 20,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 10,
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    gridItem: {
        width: '50%',
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 5,
    },
    checkbox: {
        width: 12,
        height: 12,
        borderWidth: 1,
        borderColor: '#000',
        marginRight: 5,
        justifyContent: 'center',
        alignItems: 'center',
    },
    checkText: {
        fontSize: 10,
        textAlign: 'center',
        lineHeight: 1,
    },
    checklist: {
        backgroundColor: '#f0f0f0',
        padding: 20,
        borderRadius: 5,
        marginBottom: 10
    },
    checklistItem: {
        flexDirection: 'row',
        fontSize: 12,
        marginBottom: 5,
    },
    checklistHeading: {
        fontFamily: 'Helvetica-Bold',
        marginBottom: 10,
    },
    checklistItemText: {
        flex: 1,
        
    },
    checklistItemValue: {
        fontFamily: 'Helvetica-Bold',
        width: 50,
        textAlign: 'left',
    },
    image: {
        width: '100%',
        marginBottom: 10,
    },

    projectInfoP: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 18,
        marginBottom: 10,
    },
    smallText: {
        fontSize: 12,
    },
    sectionTitle: {
        fontFamily: 'Helvetica-Bold',
        marginBottom: 10,
    },
    sectionListItem: {
        fontSize: 12,
        marginBottom: 5,
    },
    raportText: {
        fontSize: 14,
        fontWeight: 'bold',
    },
    planImage: {
        width: '60%',
        maxHeight: 380,
        alignSelf: 'center',
        marginBottom: 10,
        objectFit: 'contain',
    }
});

interface PdfDocumentProps {
    data: Project;
}

const Checkbox: React.FC<{ checked: boolean }> = ({ checked }) => (
    <View style={styles.checkbox}>
        {checked && <Text style={styles.checkText}>X</Text>}
    </View>
);

const PdfDocument: React.FC<PdfDocumentProps> = ({ data }) => (
    <Document>
        <Page style={styles.coverPage}>
            <Image src={cover1} style={styles.coverImage} />
        </Page>
        <Page style={styles.page}>
            <View style={styles.header}>
                <View style={styles.logoContainer}>
                    <Image src={logo} style={styles.logo} />
                    <Text style={styles.logoText}>Apply Heating</Text>
                </View>
                <Text style={styles.raportText}>Projectrapport</Text>
            </View>

            <View style={styles.projectInfo}>
                <Text style={{ marginBottom: 5 }}><Text style={styles.bold}>Klant:</Text> {data.klant}</Text>
                <Text style={{ marginBottom: 5 }}><Text style={styles.bold}>Adres:</Text> {data.adres}</Text>
                <Text><Text style={styles.bold}>Info:</Text> {data.info}</Text>
            </View>

            <View style={styles.section}>
                <View style={styles.sectionHeader}>
                    <Checkbox checked={!!(data.vloerverwarming && data.vloerverwarming.length > 0)} />
                    <Text style={styles.bold}>VLOERVERWARMING:</Text>
                </View>
                <View style={styles.grid}>
                    {Object.values(Vloerverwarming).map((value) => (
                        <View key={value} style={styles.gridItem}>
                            <Checkbox checked={!!(data.vloerverwarming && data.vloerverwarming.includes(value))} />
                            <Text style={styles.smallText}>{value}</Text>
                        </View>
                    ))}
                </View>
            </View>

            <View style={styles.section}>
                <View style={styles.sectionHeader}>
                    <Checkbox checked={!!data.klimaatwand} />
                    <Text style={styles.bold}>WANDKLIMAAT</Text>
                </View>
                <View style={styles.grid}>
                    {Object.values(Klimaatwand).map((value) => (
                        <View key={value} style={styles.gridItem}>
                            <Checkbox checked={data.klimaatwand === value} />
                            <Text style={styles.smallText}>{value}</Text>
                        </View>
                    ))}
                </View>
            </View>

            <View style={styles.section}>
                <View style={styles.sectionHeader}>
                    <Checkbox checked={!!data.klimaatplafond} />
                    <Text style={styles.bold}>PLAFONDKLIMAAT</Text>
                </View>
                <View style={styles.grid}>
                    {Object.values(Klimaatplafond).map((value) => (
                        <View key={value} style={styles.gridItem}>
                            <Checkbox checked={data.klimaatplafond === value} />
                            <Text style={styles.smallText}>{value}</Text>
                        </View>
                    ))}
                </View>
            </View>

            <View style={styles.checklist}>
                <Text style={styles.checklistHeading}>CHECKLIST</Text>
                <View style={styles.checklistItem}>
                    <Text style={styles.checklistItemText}>Volgens legplan:</Text>
                    <Text style={styles.checklistItemValue}>{data.volgensLegplan ? 'ja' : 'nee'}</Text>
                </View>
                <View style={styles.checklistItem}>
                    <Text style={styles.checklistItemText}>Kringnummers aangegeven op verdelers?</Text>
                    <Text style={styles.checklistItemValue}>{data.kringnummersAangegevenOpVerdelers ? 'ja' : 'nee'}</Text>
                </View>
                <View style={styles.checklistItem}>
                    <Text style={styles.checklistItemText}>Installatie dicht:</Text>
                    <Text style={styles.checklistItemValue}>{data.installatieDicht ? 'ja' : 'nee'}</Text>
                </View>
            </View>
        </Page>
            {data.sections.map((section, index) => (
                <Page key={section.id} style={styles.page}>
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>{section.sectionName}</Text>
                        <Text style={styles.sectionListItem}>• Afgeperst met: {section.afgeperstMet}</Text>
                        <Text style={styles.sectionListItem}>• Hoeveelheid druk (bar): {section.hoeveelheidDruk}</Text>
                        <Text style={styles.sectionListItem}>• Buitentemperatuur (°C): {section.buitenTemperatuur}</Text>
                    </View>
                    {section.plan && 'url' in section.plan && (
                        <Image style={styles.planImage} src={section.plan.url} />
                    )}
                    {section.images && section.images.length > 0 && (
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                            {section.images.map((image, index) => (
                                'url' in image && <Image key={index} style={{ width: '33%', maxHeight: 200, padding: 1, objectFit: 'contain' }} src={image.url} />
                            ))}
                        </View>
                    )}
                </Page>
            ))}
        <Page style={styles.coverPage}>
            <Image src={cover2} style={styles.coverImage} />
        </Page>
    </Document>
);

export default PdfDocument;
